import { type ComponentProps } from 'react'
import { F0Box, F0Icon, F0Text } from '@factorialco/f0-react'
import { F0Callout } from '@factorialco/f0-react/dist/experimental'
import {
  AlertCircle,
  CheckCircle,
  ClockBack,
  DottedCircle,
  PartiallyCompleted,
} from '@factorialco/f0-react/icons/app'

import type { IntegrationsAgreggatedSyncableStateValuesEnum } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  type AggregatedStatus,
  useTranslatedAggregatedStatus,
  useIntegrationsSyncContext,
  useSyncableStateStatistics,
} from 'modules/integrations/public'

type IconColor = 'warning' | 'critical' | 'positive' | 'default'
type CalloutVariant = 'positive' | 'critical' | 'info' | 'warning'

type StatusItem = {
  key: AggregatedStatus
  count: number
  icon: ComponentProps<typeof F0Icon>['icon']
  iconColor: IconColor
}

const SyncStatusStats = () => {
  const { isSyncRunning } = useIntegrationsSyncContext()
  const { statistics, loading } = useSyncableStateStatistics({ skip: isSyncRunning })
  const translateStatus = useTranslatedAggregatedStatus()

  if (isSyncRunning || loading || !statistics) return null

  const countFor = (status: IntegrationsAgreggatedSyncableStateValuesEnum): number =>
    statistics.statusCounts.find((sc) => sc.status === status)?.count ?? 0

  const pendingCount = countFor('pending')
  const syncedCount = countFor('synced')
  const outdatedCount = countFor('outdated')
  const partiallySyncedCount = countFor('partially_synced')
  const failedCount = countFor('failed')
  const totalCount = statistics.totalCount ?? 0

  const items: StatusItem[] = [
    {
      key: 'pending',
      count: pendingCount,
      icon: DottedCircle,
      iconColor: 'default',
    },
    {
      key: 'outdated',
      count: outdatedCount,
      icon: ClockBack,
      iconColor: 'warning',
    },
    {
      key: 'partiallySynced',
      count: partiallySyncedCount,
      icon: PartiallyCompleted,
      iconColor: 'warning',
    },
    {
      key: 'failed',
      count: failedCount,
      icon: AlertCircle,
      iconColor: 'critical',
    },
    {
      key: 'synced',
      count: syncedCount,
      icon: CheckCircle,
      iconColor: 'positive',
    },
  ]

  const visibleItems = items.filter((item) => item.count > 0)

  if (visibleItems.length === 0) return null

  const resolveCalloutConfig = (): { variant: CalloutVariant; title: string } => {
    // Up-to-date: 100% synced
    if (syncedCount === totalCount) {
      return {
        variant: 'positive',
        title: i18n.t('bookkeepers.clients.payroll_cycles.sync_status_stats.titles.all_synced'),
      }
    }

    // First time: 100% pending
    if (pendingCount === totalCount) {
      return {
        variant: 'info',
        title: i18n.t('bookkeepers.clients.payroll_cycles.sync_status_stats.titles.all_pending'),
      }
    }

    // Failed: at least 1 failed
    if (failedCount > 0) {
      return {
        variant: 'critical',
        title: i18n.t('bookkeepers.clients.payroll_cycles.sync_status_stats.titles.failed'),
      }
    }

    // Partial sync: at least 1 partially synced
    if (partiallySyncedCount > 0) {
      return {
        variant: 'warning',
        title: i18n.t(
          'bookkeepers.clients.payroll_cycles.sync_status_stats.titles.partially_synced'
        ),
      }
    }

    // New changes: at least 1 pending or outdated
    return {
      variant: 'info',
      title: i18n.t('bookkeepers.clients.payroll_cycles.sync_status_stats.titles.pending'),
    }
  }

  const { variant, title } = resolveCalloutConfig()

  return (
    <F0Box display="flex" paddingX="xl" paddingY="none" marginBottom="md">
      <F0Callout variant={variant} title={title}>
        <F0Box display="flex" flexDirection="row" alignItems="center" gap="3xl" paddingBottom="md">
          <F0Text
            variant="description"
            content={i18n.t('bookkeepers.clients.payroll_cycles.sync_status_stats.description')}
          />
        </F0Box>
        <F0Box display="flex" flexDirection="row" gap="lg" flexWrap="wrap" alignItems="center">
          {visibleItems.map((item) => (
            <F0Box key={item.key} display="flex" flexDirection="row" alignItems="center" gap="sm">
              <F0Icon icon={item.icon} size="lg" color={item.iconColor} />
              <F0Text variant="body" content={`${translateStatus(item.key)}:`} />
              <F0Text variant="label" content={`${item.count}`} />
            </F0Box>
          ))}
        </F0Box>
      </F0Callout>
    </F0Box>
  )
}

export default SyncStatusStats
